import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, X } from 'lucide-react';

export function ManualItemsTable({ items, products, canUpdateMrp, onUpdateProduct, onUpdateItem, onRemove }) {
  if (items.length === 0) {
    return (
      <div className="text-center py-8 border rounded-lg bg-gray-50">
        <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" />
        <p className="text-gray-500">No items added yet</p>
        <p className="text-sm text-gray-400">Click "Add Item" to start entering products</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-orange-50">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">Product *</th>
            <th className="px-2 py-2 text-center font-semibold w-24">Quantity *</th>
            <th className="px-2 py-2 text-center font-semibold w-20">Damaged</th>
            <th className="px-2 py-2 text-center font-semibold w-20 bg-green-100">Accepted</th>
            <th className="px-2 py-2 text-center font-semibold w-20">MRP</th>
            {canUpdateMrp && (
              <th className="px-2 py-2 text-center font-semibold w-24 bg-green-50">Update MRP</th>
            )}
            <th className="px-2 py-2 text-center font-semibold w-24">Batch #</th>
            <th className="px-2 py-2 text-center font-semibold w-24">Expiry</th>
            <th className="px-2 py-2 w-10"></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {items.map((item, idx) => (
            <ManualItemRow
              key={`manual-${item.product_id || idx}`}
              item={item}
              idx={idx}
              products={products}
              canUpdateMrp={canUpdateMrp}
              onUpdateProduct={onUpdateProduct}
              onUpdateItem={onUpdateItem}
              onRemove={onRemove}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ManualItemRow({ item, idx, products, canUpdateMrp, onUpdateProduct, onUpdateItem, onRemove }) {
  return (
    <tr className="hover:bg-orange-50/50">
      <td className="px-3 py-2">
        <Select
          value={item.product_id}
          onValueChange={(val) => onUpdateProduct(idx, val)}
        >
          <SelectTrigger className="h-8" data-testid={`manual-product-${idx}`}>
            <SelectValue placeholder="Select product..." />
          </SelectTrigger>
          <SelectContent>
            {products.map(p => (
              <SelectItem key={p.id} value={p.id}>
                {p.name} {p.barcode ? `(${p.barcode})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-2 py-2">
        <Input
          type="number"
          min="0"
          step="0.01"
          value={item.received_quantity}
          onChange={(e) => onUpdateItem(idx, 'received_quantity', e.target.value)}
          className="h-8 text-center w-full font-medium"
          data-testid={`manual-qty-${idx}`}
        />
      </td>
      <td className="px-2 py-2">
        <Input
          type="number"
          min="0"
          step="1"
          value={item.damaged_quantity}
          onChange={(e) => onUpdateItem(idx, 'damaged_quantity', e.target.value)}
          className="h-8 text-center"
          data-testid={`manual-damaged-${idx}`}
        />
      </td>
      <td className="px-2 py-2 text-center bg-green-100/50">
        <span className="font-bold text-green-700">{item.accepted_quantity}</span>
      </td>
      <td className="px-2 py-2 text-center">
        <span className="font-medium text-gray-600">{item.current_mrp || 0}</span>
      </td>
      {canUpdateMrp && (
        <td className="px-2 py-2 bg-green-50">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={item.new_mrp || ''}
            onChange={(e) => onUpdateItem(idx, 'new_mrp', e.target.value)}
            className={`h-8 text-center ${item.new_mrp !== item.current_mrp ? 'border-green-500 bg-green-100' : ''}`}
            data-testid={`manual-mrp-${idx}`}
          />
        </td>
      )}
      <td className="px-2 py-2">
        <Input
          value={item.batch_number}
          onChange={(e) => onUpdateItem(idx, 'batch_number', e.target.value)}
          placeholder="Batch"
          className="h-8 text-center text-xs"
        />
      </td>
      <td className="px-2 py-2">
        <Input
          type="date"
          value={item.expiry_date}
          onChange={(e) => onUpdateItem(idx, 'expiry_date', e.target.value)}
          className="h-8 text-xs"
        />
      </td>
      <td className="px-2 py-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onRemove(idx)}
          className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
        >
          <X size={14} />
        </Button>
      </td>
    </tr>
  );
}
